import { useCallback, useMemo } from 'react';
import { getMergedSelectedSectionSettings } from 'src/components/Listings/InventoryEventPage/InventoryEventDetail/InventoryEventDetail.hooks';
import { useInventoryEventDetailContext } from 'src/contexts/InventoryEventDetailContext/InventoryEventDetailContext';
import {
  mapToSelectedSectionSettings,
  SelectedSectionSettingsWithOptionals,
} from 'src/utils/types/selectedSectionSettingsWithOptionals';
import { SectionInfo } from 'src/WebApiController';

import { useAutoPricingSettingsFormContext } from '../useAutoPricingSettingsFormContext';

export const useAutoPricingSettingsSelectedSections = (
  venueMapSections: SectionInfo[] | null | undefined
) => {
  const { setValue, watch } = useAutoPricingSettingsFormContext();

  const {
    isRecommendationDeclined,
    onToggleRecommendingSectionIds,
    sectionRowScoreRecommendations,
  } = useInventoryEventDetailContext();

  const { compListingSelectedSectionSettings } = watch();

  const selectedSections = useMemo(() => {
    const formSectionFilterIds =
      compListingSelectedSectionSettings?.sectionIdFilter ?? [];
    return (
      venueMapSections?.filter((section) => {
        return formSectionFilterIds.includes(section.id);
      }) ?? []
    );
  }, [compListingSelectedSectionSettings?.sectionIdFilter, venueMapSections]);

  const rowIdFilters = useMemo(() => {
    return compListingSelectedSectionSettings?.rowIdFilter ?? [];
  }, [compListingSelectedSectionSettings]);

  const sectionRowIdFilters = useMemo(() => {
    return compListingSelectedSectionSettings?.sectionRowIdFilter ?? {};
  }, [compListingSelectedSectionSettings]);

  const onRowIdFiltersChange = useCallback(
    (rowIdFilters: number[], sectionRowIdFilters: { [x: string]: string }) => {
      const compListingSelectedSectionSettingsNew = {
        sectionIdFilter:
          compListingSelectedSectionSettings?.sectionIdFilter ?? [],
        rowIdFilter: Array.from(new Set(rowIdFilters)),
        sectionRowIdFilter: sectionRowIdFilters,
      };

      setValue(
        'compListingSelectedSectionSettings',
        mapToSelectedSectionSettings(compListingSelectedSectionSettingsNew) ??
          null
      );
      setValue('compListingOnlyForSelectedSectionsEnabled', true);
    },
    [compListingSelectedSectionSettings?.sectionIdFilter, setValue]
  );

  const onSectionIdFiltersChange = useCallback(
    (
      sectionId: number,
      selectedSections: SectionInfo[],
      section?: SectionInfo
    ) => {
      let newSectionIdFilter =
        compListingSelectedSectionSettings?.sectionIdFilter ?? [];

      let newRowIdFilter =
        compListingSelectedSectionSettings?.rowIdFilter ?? [];

      let newSectionRowIdFilter =
        compListingSelectedSectionSettings?.sectionRowIdFilter ?? {};

      if (newSectionIdFilter.includes(sectionId)) {
        newSectionIdFilter = newSectionIdFilter.filter(
          (id) => id !== sectionId
        );

        // Remove all the associated row ids for this section
        const section = selectedSections.find((s) => s.id === sectionId);
        if (section) {
          const rowIds = section.rows.map((r) => r.id);
          newRowIdFilter = newRowIdFilter.filter((id) => !rowIds.includes(id));
        }

        // Remove the sectionRow mapping so we don't display it in pills
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [sectionId]: omitted, ...updatedSectionRowIdFilter } =
          compListingSelectedSectionSettings?.sectionRowIdFilter ?? {};

        newSectionRowIdFilter = updatedSectionRowIdFilter;
      } else {
        newSectionIdFilter.push(sectionId);

        // If a section is present, add all the rows associated with the newly selected section
        if (section) {
          const newRowIds = section.rows.map((r) => r.id);
          newRowIds.forEach((newRowId) => {
            if (!newRowIdFilter.includes(newRowId)) {
              newRowIdFilter.push(newRowId);
            }
          });
        }
      }

      onToggleRecommendingSectionIds?.([sectionId]);

      const compListingSelectedSectionSettingsNew = {
        ...compListingSelectedSectionSettings,
        sectionIdFilter: newSectionIdFilter,
        rowIdFilter: newRowIdFilter,
        sectionRowIdFilter: newSectionRowIdFilter,
      };

      const sanitizedSectionSettings =
        mapToSelectedSectionSettings(compListingSelectedSectionSettingsNew) ??
        null;

      setValue(
        'compListingSelectedSectionSettings',
        isRecommendationDeclined
          ? sanitizedSectionSettings
          : (getMergedSelectedSectionSettings(
              sanitizedSectionSettings,
              sectionRowScoreRecommendations?.recommendedSelectedSectionSettings
            ) ?? null)
      );
    },
    [
      compListingSelectedSectionSettings,
      isRecommendationDeclined,
      onToggleRecommendingSectionIds,
      sectionRowScoreRecommendations?.recommendedSelectedSectionSettings,
      setValue,
    ]
  );

  const setCompListingSelectedSectionSettings = useCallback(
    (
      compListingSelectedSectionSettingsNew: SelectedSectionSettingsWithOptionals | null
    ) => {
      setValue(
        'compListingSelectedSectionSettings',
        mapToSelectedSectionSettings(compListingSelectedSectionSettingsNew) ??
          null
      );
    },
    [setValue]
  );

  const onRowOffsetChange = useCallback(
    (rowOffset: number | undefined) => {
      if (!compListingSelectedSectionSettings) {
        return;
      }
      const compListingSelectedSectionSettingsNew = {
        ...compListingSelectedSectionSettings,
        rowIdFilter: null,
        sectionRowIdFilter: null,
        rowOffset,
      };

      const sanitizedSectionSettings =
        mapToSelectedSectionSettings(compListingSelectedSectionSettingsNew) ??
        null;

      const mergedSectionSettings = isRecommendationDeclined
        ? sanitizedSectionSettings
        : (getMergedSelectedSectionSettings(
            sanitizedSectionSettings,
            sectionRowScoreRecommendations?.recommendedSelectedSectionSettings,
            true
          ) ?? null);

      setValue('compListingSelectedSectionSettings', mergedSectionSettings);
    },
    [
      compListingSelectedSectionSettings,
      isRecommendationDeclined,
      sectionRowScoreRecommendations?.recommendedSelectedSectionSettings,
      setValue,
    ]
  );

  return {
    rowOffset: compListingSelectedSectionSettings?.rowOffset,
    selectedSections,
    rowIdFilters,
    sectionRowIdFilters,
    onRowIdFiltersChange,
    onSectionIdFiltersChange,
    compListingSelectedSectionSettings,
    setCompListingSelectedSectionSettings,
    onRowOffsetChange,
  };
};
